import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogIn, Moon, Sun, ArrowLeft, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SurveysPage() {
  const [surveys, setSurveys] = useState([]);
  const [currentSurvey, setCurrentSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      const response = await axios.get(`${API}/surveys/active`);
      setSurveys(response.data);
    } catch (error) {
      console.error('Failed to fetch surveys:', error);
    }
  };

  const handleStartSurvey = (survey) => {
    setCurrentSurvey(survey);
    setAnswers({});
  };

  const handleAnswerChange = (questionIndex, value) => {
    setAnswers({ ...answers, [questionIndex]: value });
  };

  const handleSubmitSurvey = async (e) => {
    e.preventDefault();

    // Check if all questions are answered
    if (Object.keys(answers).length !== currentSurvey.questions.length) {
      toast.error('Please answer all questions');
      return;
    }

    try {
      await axios.post(`${API}/surveys/respond`, {
        survey_id: currentSurvey.id,
        answers
      });
      toast.success('Thank you for your feedback!');
      setCurrentSurvey(null);
      setAnswers({});
    } catch (error) {
      toast.error('Failed to submit survey');
    }
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-[#020617]' : 'bg-slate-50'}`}>
      {/* Header */}
      <div className={`sticky top-0 z-10 ${theme === 'dark' ? 'glassmorphism' : 'bg-white/90 backdrop-blur-sm border-b border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="icon"
              data-testid="back-to-menu-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className={`font-serif text-2xl md:text-3xl ${theme === 'dark' ? 'text-amber-50' : 'text-slate-900'}`} data-testid="surveys-page-title">
              Customer Surveys
            </h1>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={toggleTheme}
              variant="ghost"
              size="icon"
              className={theme === 'dark' ? 'text-amber-50 hover:text-amber-400' : 'text-slate-900 hover:text-amber-600'}
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        {!currentSurvey ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {surveys.map((survey, index) => (
              <motion.div
                key={survey.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className={`${theme === 'dark' ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`} data-testid={`survey-card-${survey.id}`}>
                  <CardHeader>
                    <CardTitle className={theme === 'dark' ? 'text-amber-50' : 'text-slate-900'}>{survey.title}</CardTitle>
                    <CardDescription>{survey.questions.length} questions</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      onClick={() => handleStartSurvey(survey)}
                      className="w-full bg-amber-500 hover:bg-amber-400 text-black"
                      data-testid={`start-survey-${survey.id}`}
                    >
                      Take Survey
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}

            {surveys.length === 0 && (
              <div className="text-center py-16" data-testid="no-surveys-message">
                <p className={`text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
                  No active surveys at the moment.
                </p>
              </div>
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className={`${theme === 'dark' ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className={theme === 'dark' ? 'text-amber-50' : 'text-slate-900'}>{currentSurvey.title}</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentSurvey(null)}
                    data-testid="close-survey-button"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitSurvey} className="space-y-6">
                  {currentSurvey.questions.map((question, index) => (
                    <div key={index} className="space-y-2" data-testid={`question-${index}`}>
                      <Label className={`text-base ${theme === 'dark' ? 'text-amber-50' : 'text-slate-900'}`}>
                        {index + 1}. {question}
                      </Label>
                      <Textarea
                        value={answers[index] || ''}
                        onChange={(e) => handleAnswerChange(index, e.target.value)}
                        placeholder="Your answer..."
                        rows={3}
                        required
                        data-testid={`answer-${index}`}
                      />
                    </div>
                  ))}
                  <Button
                    type="submit"
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                    data-testid="submit-survey-button"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Submit Survey
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
